package com.alpha.showcase.common.ui.view

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowRight
import androidx.compose.material.icons.outlined.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment.Companion.CenterVertically
import androidx.compose.ui.ExperimentalComposeUiApi
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.scale
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.type
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import usesRemoteNavigation
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import kotlin.math.abs
import kotlin.math.floor
import kotlin.math.roundToInt


val ITEM_PADDING_HORIZONTAL = 18.dp
val ITEM_PADDING_VERTICAL = 12.dp

@Immutable
data class LevelOption(
  val value: Int,
  val label: String
)

internal class LevelSelectionState(
  value: Int,
  private val levels: List<LevelOption>
) {
  var selectedIndex by mutableIntStateOf(nearestLevelIndex(value, levels))
    private set

  private val pendingEmissions = mutableListOf<Int>()

  fun selectIndex(index: Int): Int? {
    val nextIndex = index.coerceIn(levels.indices)
    if (nextIndex == selectedIndex) return null

    selectedIndex = nextIndex
    val nextValue = levels[nextIndex].value
    pendingEmissions += nextValue
    return nextValue
  }

  fun syncExternalValue(externalValue: Int) {
    val echoIndex = pendingEmissions.indexOf(externalValue)
    if (echoIndex >= 0) {
      pendingEmissions.subList(0, echoIndex + 1).clear()
      return
    }

    pendingEmissions.clear()
    selectedIndex = nearestLevelIndex(externalValue, levels)
  }
}

internal fun nearestLevelIndex(value: Int, levels: List<LevelOption>): Int {
  return levels.indices.minByOrNull { index ->
    abs(levels[index].value - value)
  } ?: 0
}


@Composable
fun CommonItem(desc: String, content: @Composable RowScope.() -> Unit) {
  Row(
    modifier = Modifier
      .padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL)
      .wrapContentHeight()
      .fillMaxWidth(),
    verticalAlignment = CenterVertically
  ) {
    Text(
      text = desc, modifier = Modifier
        .weight(1f)
        .padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL)
    )
    content()
  }
}

@Composable
fun IconItem(icon: Any, desc: String, onClick: (() -> Unit)? = null, content: @Composable RowScope.() -> Unit = {}) {
  val performHaptic = rememberMobileHaptic()

  if (onClick == null) {
    Surface(
      modifier = Modifier
        .fillMaxWidth(),
      shape = CardDefaults.shape
    ) {
      ItemContent(icon = icon, desc = desc) {
        content()
      }
    }
  } else {
    Surface(
      modifier = Modifier
        .fillMaxWidth(),
      shape = CardDefaults.shape,
      onClick = {
        performHaptic()
        onClick()
      }
    ) {
      ItemContent(icon = icon, desc = desc) {
        content()
      }
    }
  }

}

@Composable
fun ItemContent(icon: Any?, desc: String, content: @Composable (RowScope.() -> Unit)? = null) {
  Row(
    modifier = Modifier
      .padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL)
      .fillMaxWidth(),
    verticalAlignment = CenterVertically
  ) {

    when (icon) {
      is ImageVector -> {
        Icon(
          imageVector = icon,
          contentDescription = desc,
          modifier = Modifier
            .padding(5.dp)
        )
      }

      is Painter -> {
        Icon(
          painter = icon,
          contentDescription = desc,
          modifier = Modifier
            .padding(5.dp)
        )
      }

      is DrawableResource -> {
        Icon(
          painter = painterResource(icon),
          contentDescription = desc,
          modifier = Modifier
            .padding(5.dp)
        )
      }

      else -> {
//        CircularProgressIndicator(
//          modifier = Modifier
//            .padding(5.dp)
//        )
      }
    }

    Text(
      text = desc, modifier = Modifier
        .weight(1f)
        .padding(15.dp)
    )
    content?.invoke(this)
  }
}


@Composable
fun SwitchItem(icon: Any, check: Boolean, desc: String, onCheck: (Boolean) -> Unit) {
  var checked by remember {mutableStateOf(check)}
  val performHaptic = rememberMobileHaptic()

  val thumbContent: (@Composable () -> Unit)? = if (checked) {
    {
      Icon(
        imageVector = Icons.Outlined.Check,
        contentDescription = null,
        modifier = Modifier.size(SwitchDefaults.IconSize),
      )
    }
  } else {
    null
  }

  IconItem(icon = icon, desc = desc, onClick = {
    checked = ! checked
    onCheck(checked)
  }) {
    Switch(
      modifier = Modifier
        .padding(5.dp)
        .semantics { contentDescription = desc },
      checked = checked,
      onCheckedChange = {
        performHaptic()
        checked = it
        onCheck(checked)
      },
      thumbContent = thumbContent)
  }
}

@Composable
fun <T> CheckItem(icon: Any, value: Pair<T, String>, desc: String, choices: List<Pair<T, String>>, onCheck: (Pair<T, String>) -> Unit) {
  var expanded by remember {mutableStateOf(false)}
  val check by rememberUpdatedState(value)
  val performHaptic = rememberMobileHaptic()
  val windowHeight = with(LocalDensity.current) {
    LocalWindowInfo.current.containerSize.height.coerceAtLeast(0).toDp()
  }
  val safePadding = WindowInsets.safeDrawing.asPaddingValues()
  val menuMaxHeight = (windowHeight - safePadding.calculateTopPadding() -
    safePadding.calculateBottomPadding()).coerceAtLeast(0.dp)

  val checkString by remember {
    derivedStateOf {
      check.second
    }
  }


  IconItem(icon = icon, desc = desc, onClick = {
    expanded = ! expanded
  }) {

    Text(text = checkString)
    Box {
      IconButton(onClick = {
        performHaptic()
        expanded = ! expanded
      }) {
        Icon(Icons.AutoMirrored.Outlined.ArrowRight, contentDescription = desc)
      }
        DropdownMenu(
            // iOS can measure a new popup before its scene has finite height constraints.
            modifier = Modifier.heightIn(max = menuMaxHeight),
            shape = RoundedCornerShape(16.dp),
            expanded = expanded,
            onDismissRequest = {expanded = false},
            tonalElevation = 6.dp,
            shadowElevation = 6.dp
        ){

        choices.forEachIndexed {index, item ->
          DropdownMenuItem(
            text = {Text(item.second, modifier = Modifier.fillMaxSize(), textAlign = TextAlign.Center)},
            onClick = {
              performHaptic()
              expanded = false
              onCheck(item)
            },
            //            leadingIcon = {
            //              Icon(
            //                Icons.Outlined.FiberManualRecord,
            //                contentDescription = checkString
            //              )
            //            }
          )
        }
      }
    }
  }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SlideItem(
  icon: Any,
  trigger: Boolean? = null,
  onCheck: ((Boolean) -> Unit)? = null,
  desc: String,
  value: Int,
  range: ClosedFloatingPointRange<Float>,
  unit: String = "",
  step: Int = 0,
  onClick: (() -> Unit)? = null,
  onValueChanged: (Int) -> Unit,
) {

  var checked by remember {
    mutableStateOf(trigger ?: true)
  }
  val performHaptic = rememberMobileHaptic()


  val thumbContent: (@Composable () -> Unit)? = if (checked) {
    {
      Icon(
        imageVector = Icons.Outlined.Check,
        contentDescription = null,
        modifier = Modifier.size(SwitchDefaults.IconSize),
      )
    }
  } else {
    null
  }

  Surface(
    modifier = Modifier
      .fillMaxWidth(),
    shape = CardDefaults.shape
  ) {
    Column(modifier = Modifier.fillMaxWidth()) {

      var sliderPosition by remember(value) {mutableStateOf(value)}
      val text = if (checked) "$sliderPosition $unit" else ""

      if (trigger == null) {
        IconItem(icon = icon, desc = desc, onClick = onClick) {
          Text(
            text = if (checked) "${sliderPosition} $unit" else "",
            Modifier.padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL)
          )
        }
      } else {
        Surface(modifier = Modifier
          .background(Color.Transparent)
          .fillMaxWidth()
          .wrapContentHeight(), onClick = {
          performHaptic()
          checked = ! checked
          onCheck?.invoke(checked)
        }) {
          ItemContent(icon = icon, desc = desc) {
            Text(text = text, Modifier.padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL))
            Switch(
              modifier = Modifier
                .padding(5.dp)
                .semantics { contentDescription = desc },
              checked = checked,
              onCheckedChange = {
                performHaptic()
                checked = it
                onCheck?.invoke(checked)
//                if (checked) {
//                  onValueChanged.invoke(value)
//                }
              },
              thumbContent = thumbContent)
          }
        }
      }


      var sliderFocused by remember{
        mutableStateOf(false)
      }
      val interactionSource = remember { MutableInteractionSource() }

      Slider(
        modifier = Modifier
          .semantics { contentDescription = text }
          .padding(ITEM_PADDING_HORIZONTAL / 2 * 3, 0.dp).onFocusChanged {
             sliderFocused = it.isFocused
          }.onKeyEvent {
            if (sliderFocused){
              when {
                (Key.SystemNavigationLeft == it.key || it.key.keyCode == 90194313216) -> {
                  if (KeyEventType.KeyUp == it.type){
                    val toInt = sliderPosition - (if (step == 0) 1 else floor((range.endInclusive - range.start) / step)).toInt()
                    sliderPosition = if (toInt <= range.start) range.start.toInt() else toInt
                    onValueChanged(sliderPosition)
                    true
                  }else {
                    true
                  }
                }

                (Key.SystemNavigationRight == it.key || it.key.keyCode == 94489280512) -> {
                  if (KeyEventType.KeyUp == it.type){
                    val toInt = sliderPosition + (if (step == 0) 1 else floor((range.endInclusive - range.start) / step)).toInt()
                    sliderPosition = if (toInt >= range.endInclusive) range.endInclusive.toInt() else toInt
                    onValueChanged(sliderPosition)
                    true
                  }else {
                    true
                  }

                }

                else -> false
              }
            }else {
              false
            }

          },
        value = sliderPosition.toFloat(),
        onValueChange = {
          sliderPosition = it.toInt()
          onValueChanged(it.toInt())
        },
        valueRange = range,
        onValueChangeFinished = {
          performHaptic()
        },
        steps = step,
        interactionSource = interactionSource,
        thumb = {
          if (usesRemoteNavigation()) {
            TvSliderThumb(focused = sliderFocused, enabled = checked)
          } else {
            SliderDefaults.Thumb(interactionSource = interactionSource, enabled = checked)
          }
        },
        enabled = checked
      )

    }
  }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LevelSliderItem(
  icon: Any,
  desc: String,
  value: Int,
  levels: List<LevelOption>,
  onValueChanged: (Int) -> Unit
) {
  if (levels.isEmpty()) return

  val selectionState = remember(levels) {
    LevelSelectionState(value, levels)
  }
  LaunchedEffect(selectionState, value) {
    selectionState.syncExternalValue(value)
  }
  val selectedIndex = selectionState.selectedIndex
  val selectedOption = levels[selectedIndex]
  var sliderFocused by remember { mutableStateOf(false) }
  var dragged by remember { mutableStateOf(false) }
  val focusScale by animateFloatAsState(if (sliderFocused) 1.05f else 1f)
  val dragScale by animateFloatAsState(if (dragged) 1.1f else 1f)
  val interactionSource = remember { MutableInteractionSource() }
  val performHaptic = rememberMobileHaptic()

  fun selectLevel(index: Int) {
    selectionState.selectIndex(index)?.let { nextValue ->
      performHaptic()
      onValueChanged(nextValue)
    }
  }

  Surface(
    modifier = Modifier.fillMaxWidth(),
    color = Color.Transparent,
    shape = CardDefaults.shape
  ) {
    Column(modifier = Modifier.fillMaxWidth()) {
      IconItem(icon = icon, desc = desc) {
        Text(
          text = selectedOption.label,
          modifier = Modifier.padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL)
        )
      }
      Slider(
        modifier = Modifier
          .semantics { contentDescription = "$desc: ${selectedOption.label}" }
          .padding(
            start = ITEM_PADDING_HORIZONTAL / 2 * 3,
            end = ITEM_PADDING_HORIZONTAL / 2 * 3,
            bottom = ITEM_PADDING_VERTICAL / 2
          )
          .scale(if (usesRemoteNavigation()) 1f else focusScale)
          .onFocusChanged { sliderFocused = it.isFocused }
          .onKeyEvent { event ->
            if (!sliderFocused) {
              false
            } else {
              when {
                event.key == Key.SystemNavigationLeft || event.key == Key.DirectionLeft -> {
                  if (event.type == KeyEventType.KeyUp) {
                    selectLevel(selectedIndex - 1)
                  }
                  true
                }

                event.key == Key.SystemNavigationRight || event.key == Key.DirectionRight -> {
                  if (event.type == KeyEventType.KeyUp) {
                    selectLevel(selectedIndex + 1)
                  }
                  true
                }

                else -> false
              }
            }
          },
        value = selectedIndex.toFloat(),
        onValueChange = { position ->
          selectLevel(position.roundToInt())
          dragged = true
        },
        onValueChangeFinished = { dragged = false },
        valueRange = 0f..levels.lastIndex.toFloat(),
        steps = (levels.size - 2).coerceAtLeast(0),
        thumb = {
          if (usesRemoteNavigation()) {
            TvSliderThumb(focused = sliderFocused)
          } else {
            SliderDefaults.Thumb(
              modifier = Modifier.scale(dragScale),
              interactionSource = interactionSource,
              colors = SliderDefaults.colors(),
              enabled = true
            )
          }
        }
      )
    }
  }
}

@Composable
private fun TvSliderThumb(focused: Boolean, enabled: Boolean = true) {
  val focusScale by animateFloatAsState(
    targetValue = if (enabled && focused) 1.2f else 1f,
    animationSpec = tween(durationMillis = 180),
    label = "TV slider thumb focus"
  )
  val colors = SliderDefaults.colors()

  // The default Material thumb halves its width on focus. Keep its resting size
  // and reserve space for the 1.2x drawing so focus neither shifts the track nor
  // clips the thumb at the edge of its settings row or scroll viewport.
  Box(
    Modifier
      .padding(vertical = 5.dp)
      .size(width = 4.dp, height = 44.dp)
      .scale(focusScale)
      .background(if (enabled) colors.thumbColor else colors.disabledThumbColor, CircleShape)
  )
}

@Composable
fun <T> MultiCheckContent(icon: Any, desc: String, checkContent: List<List<Pair<T, String>>>, checkContentDesc: List<String>, checked: List<Pair<T, String>>, checkIcons: List<Any>, onCheckChanged: ((Int, Pair<T, String>) -> Unit), onCheckResult: (List<Pair<T, String>>) -> String){
  
  Surface {
    val resultList = remember {
      checked.toMutableStateList()
    }

    var result by remember {
      mutableStateOf(onCheckResult.invoke(checked))
    }
    
    Column {
      ItemContent(icon = icon, desc) {
        Text(text = result, Modifier.padding(ITEM_PADDING_HORIZONTAL, ITEM_PADDING_VERTICAL))
      }
      
      checkContent.forEachIndexed { index, item ->
        CheckItem(icon = checkIcons[index] , value = checked[index], desc = checkContentDesc[index], choices = item){
          resultList[index] = it
          onCheckChanged.invoke(index, it)
          result = onCheckResult.invoke(resultList.toList())
        }
      }
      
    }
    
  }

}
